import React, { useState, useEffect } from "react";

import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import Chip from "@mui/material/Chip";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { Divider, FormControl, MenuItem, Select, Tooltip, Typography } from "@mui/material";
import { Grid } from "@mui/material";
import { Alert } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import EventIcon from "@mui/icons-material/Event";
import CommentIcon from "@mui/icons-material/Comment";
import {
  APPOINTMENT_MANAGEMENT_APPOINTMENT_AGENTS,
  APPOINTMENT_MANAGEMENT_ASSIGN_APPOINTMENT_AGENT,
  APPOINTMENT_MANAGEMENT_GOOGLE_CALENDAR_APPOINTMENT_CHECK,
  APPOINTMENTS_ENDPOINT,
} from "../../config/urls";

// Filter buttons
import ButtonGroup from "@mui/material/ButtonGroup";
import { useAuth } from "../../hooks/AuthProvider";
import { PersonAdd } from "@mui/icons-material";
import { ColorsConstants } from "../../cssUtils/constants";
import AppTable from "../../common/app-table";
import CustomTabs from "../../common/custom-tabs";
import typography from "../../cssUtils/typography";
import { formatWithTzLabel } from "../../utils/dateUtils";

const columns = [
  { id: "appointment_id", label: "Appointment ID" },
  { id: "client_name", label: "Client Name" },
  // { id: 'doctor', label: 'Doctor' },
  { id: "phone", label: "Phone" },
  { id: "date", label: "Date" },
  { id: "time", label: "Time" },
  // { id: 'property_type', label: 'Property Type' },
  // { id: 'agent', label: 'Agent' },
  { id: "status", label: "Status" },
  { id: "agent", label: "Agent" },
  { id: "actions", label: "Actions" },
];

export default function AppointmentManagementTable({
  refreshKey,
  searchQuery,
}) {
  const { getValidToken } = useAuth();
  const role = localStorage.getItem("role");
  const isAdmin = role === "admin";

  // State for appointments data
  const [appointmentsData, setAppointmentsData] = useState([]);
  const [filteredStatus, setFilteredStatus] = useState("all");
  const [totalCount, setTotalCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [confirmedCount, setConfirmedCount] = useState(0);
  const [cancelledCount, setCancelledCount] = useState(0);

  // State for pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // State for modals
  const [openModal, setOpenModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [cancelAppointmentModalOpen, setCancelAppointmentModalOpen] =
    useState(false);
  const [appointmentConflictModalOpen, setAppointmentConflictModalOpen] =
    useState(false);
  const [assignAgentModalOpen, setAssignAgentModalOpen] = useState(false);

  // State for agent assignment
  const [agentOptions, setAgentOptions] = useState([]);
  const [selectedAgentOption, setSelectedAgentOption] = useState(null);

  // State for conflicting appointments
  const [conflictingAppointments, setConflictingAppointments] = useState([]);

  // State for alert
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertSeverity, setAlertSeverity] = useState("success");

  // Function to get the token (replace with your authentication method)
  const getToken = () => {
    return localStorage.getItem("token");
  };

  // Function to format date to day, month date, year
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";

    // Create a date object with the time string
    // We use a dummy date because we only require the time portion
    const dateObj = new Date(`2000-01-01T${timeString}`);

    // Use toLocaleTimeString to format the time in 12-hour format with AM/PM
    return dateObj.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Function to fetch appointments data
  const fetchAppointmentsData = async () => {
    const token = getToken();
    let endpoint = `${APPOINTMENTS_ENDPOINT}/appointments?limit=${rowsPerPage}&offset=${page * rowsPerPage}`;

    if (filteredStatus !== "all") {
      endpoint += `&status=${filteredStatus}`;
    }

    if (searchQuery) {
      endpoint += `&search=${searchQuery}`;
    }

    try {
      const response = await fetch(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": localStorage.getItem("tenant"),
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch appointments data");
      }

      const jsonData = await response.json();
      setAppointmentsData(jsonData.results || []);
      setTotalCount(jsonData.total_count || 0);
      setPendingCount(jsonData.pending_count || 0);
      setConfirmedCount(jsonData.confirmed_count || 0);
      setCancelledCount(jsonData.cancelled_count || 0);
    } catch (error) {
      console.error("Error fetching appointments data:", error.message);
      setAlertMessage("Failed to load appointments. Please try again.");
      setAlertSeverity("error");
    }
  };

  // Function to update appointment status
  const updateAppointmentStatus = async (appointmentId, newStatus) => {
    const token = getToken();
    const endpoint = `${APPOINTMENTS_ENDPOINT}/appointments/${appointmentId}/${newStatus.toLowerCase()}/?role=${role}`;

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": localStorage.getItem("tenant"),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to ${newStatus.toLowerCase()} appointment`);
      }

      const data = await response.json();

      if (data && data.status === "failed" && data.error) {
        setAlertMessage(data.error);
        setAlertSeverity("error");
        setOpenModal(false);
        return;
      }

      // Close modal and refresh data
      setOpenModal(false);
      fetchAppointmentsData();
      if (newStatus.toLowerCase() === "confirm") {
        setAlertMessage(`Appointment confirmed successfully`);
      } else if (newStatus.toLowerCase() === "cancel") {
        setAlertMessage(`Appointment cancelled successfully`);
      } else {
        setAlertMessage(`Appointment status updated successfully`);
      }
      setAlertSeverity("success");

      // Clear alert after 3 seconds
      setTimeout(() => {
        setAlertMessage(null);
      }, 3000);
    } catch (error) {
      console.error(
        `Error ${newStatus.toLowerCase()} appointment:`,
        error.message,
      );
      setAlertMessage(
        `Failed to ${newStatus.toLowerCase()} appointment. Please try again.`,
      );
      setAlertSeverity("error");
    }
  };

  const updateAgentAssignment = async () => {
    const tenant = localStorage.getItem("tenant");
    const token = await getValidToken();
    const endpoint = `${APPOINTMENT_MANAGEMENT_ASSIGN_APPOINTMENT_AGENT}`;

    if (!selectedAppointment) {
      setAlertMessage("Please select an appointment to assign.");
      setAlertSeverity("error");
      return;
    }

    if (!selectedAgentOption) {
      setAlertMessage("Please select an agent to assign.");
      setAlertSeverity("error");
      return;
    }

    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      const body = {
        appointment_id: selectedAppointment.id,
        agent_id: selectedAgentOption.id,
      };

      const response = await fetch(endpoint, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`Failed to update agent assignment`);
      }

      const data = await response.json();

      if (data && data.status === "failed" && data.error) {
        setAlertMessage(data.error);
        setAlertSeverity("error");
        setOpenModal(false);
        return;
      }

      // Close modal and refresh data
      setOpenModal(false);
      fetchAppointmentsData();
      setAlertMessage(`Agent assignment updated successfully`);
      setAlertSeverity("success");
    } catch (error) {
      console.error("Error updating agent assignment:", error.message);
      setAlertMessage("Failed to update agent assignment. Please try again.");
      setAlertSeverity("error");
    }
  };

  const checkExistingAppointments = async (startTime, endTime) => {
    const tenant = localStorage.getItem("tenant");
    const token = await getValidToken();
    const endpoint = `${APPOINTMENT_MANAGEMENT_GOOGLE_CALENDAR_APPOINTMENT_CHECK}?start=${startTime}&end=${endTime}`;

    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      const response = await fetch(endpoint, {
        method: "GET",
        headers: headers,
      });

      if (!response.ok && response.status !== 403) {
        throw new Error(`Failed to check existing appointments`);
      }

      const data = await response.json();

      return data;
    } catch (error) {
      console.error("Error checking existing appointments:", error.message);
      setAlertMessage(
        "Failed to check existing appointments. Please try again.",
      );
      setAlertSeverity("error");
    }
  };

  const fetchAppointmentAgents = async () => {
    const tenant = localStorage.getItem("tenant");
    const token = await getValidToken();
    const endpoint = `${APPOINTMENT_MANAGEMENT_APPOINTMENT_AGENTS}`;

    setAgentOptions([]);
    setSelectedAgentOption(null);

    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      const response = await fetch(endpoint, {
        method: "GET",
        headers: headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch appointment agents`);
      }

      const data = await response.json();

      setAgentOptions(data.agents);
    } catch (error) {
      console.error("Error fetching appointment agents:", error.message);
    }
  };

  // Handle status filter change
  const handleFilterChange = (status) => {
    setFilteredStatus(status);
    setPage(0); // Reset to first page when filter changes
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Open modal with appointment details
  const handleOpenModal = (appointment) => {
    setSelectedAppointment(appointment);
    setOpenModal(true);
  };

  // Close the modal
  const handleCloseModal = () => {
    setOpenModal(false);
  };

  // Confirm appointment from table
  const handleConfirmAppointment = async (appointment, event) => {
    event.stopPropagation();

    setConflictingAppointments([]);

    const startTime = new Date(
      `${appointment.date}T${appointment.from_ts}`,
    ).toISOString();
    const endTime = new Date(
      `${appointment.date}T${appointment.to_ts}`,
    ).toISOString();

    const existingAppointmentsData = await checkExistingAppointments(
      startTime,
      endTime,
    );

    if (existingAppointmentsData) {
      if (!existingAppointmentsData.hasAppointments) {
        updateAppointmentStatus(appointment.id, "confirm");
      } else {
        setConflictingAppointments(existingAppointmentsData.appointments);
        setAppointmentConflictModalOpen(true);
        setSelectedAppointment(appointment);
      }
    } else {
      console.error("Error checking existing appointments");
    }
  };

  // Assign agent
  const handleAssignAgent = async (appointment, event) => {
    event.stopPropagation();

    await fetchAppointmentAgents();

    setAssignAgentModalOpen(true);
    setSelectedAppointment(appointment);
  };

  // Cancel appointment from table
  const handleCancelAppointment = (appointment, event) => {
    event.stopPropagation();
    setCancelAppointmentModalOpen(true);
    setSelectedAppointment(appointment);
  };

  // Close the assign agent modal
  const handleCloseAssignAgentModal = () => {
    setAssignAgentModalOpen(false);
  };

  // Close the cancel appointment modal
  const handleCloseCancelAppointmentModal = () => {
    setCancelAppointmentModalOpen(false);
  };

  // Close the conflict appointment modal
  const handleCloseConflictAppointmentModal = () => {
    setAppointmentConflictModalOpen(false);
  };

  // Fetch data when dependencies change
  useEffect(() => {
    fetchAppointmentsData();
  }, [page, rowsPerPage, filteredStatus, searchQuery, refreshKey]);

  // Modal style
  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 600,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
  };

   
  const tableColumns = React.useMemo(() => [
    {
      id: "id",
      label: "Appointment ID",
      render: (appointment) => `APT-${appointment.id}`,
    },
    { id: "client_name", label: "Client Name"  },
    { id: "phone_no",    label: "Phone"        },
    {
      id: "date",
      label: "Date",
      render: (appointment) => formatDate(appointment.date),
    },
    {
      id: "time",
      label: "Time",
      render: (appointment) =>
        `${formatTime(appointment.from_ts)} - ${formatTime(appointment.to_ts)}`,
    },
    {
      id: "appointment_status",
      label: "Status",
      render: (appointment) => (
        <Chip
          label={appointment.appointment_status}
          color={
            appointment.appointment_status === "Confirmed" ? "success"
            : appointment.appointment_status === "Cancelled" ? "error"
            : "warning"
          }
          variant="outlined"
          size="small"
        />
      ),
    },
    {
      id: "agent",
      label: "Agent",
      render: (appointment) =>
        appointment.agent_name || appointment.agent_email || "N/A",
    },
    {
      id: "__actions__",
      label: "Actions",
      render: (appointment) => (
        <Box display="flex" gap={0.5}>
          <Tooltip title="View Details">
            <IconButton size="small" color="primary"
              onClick={(e) => { e.stopPropagation(); handleOpenModal(appointment); }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
  
          {appointment.appointment_status !== "Confirmed" && (
            <Tooltip title="Confirm Appointment">
              <IconButton size="small" color="success"
                onClick={(e) => { e.stopPropagation(); handleConfirmAppointment(appointment, e); }}
              >
                <CheckIcon />
              </IconButton>
            </Tooltip>
          )}
  
          {appointment.appointment_status !== "Confirmed" && isAdmin && (
            <Tooltip title="Assign Agent">
              <IconButton size="small" color="primary"
                onClick={(e) => { e.stopPropagation(); handleAssignAgent(appointment, e); }}
              >
                <PersonAdd />
              </IconButton>
            </Tooltip>
          )}
  
          {appointment.appointment_status !== "Cancelled" && (
            <Tooltip title="Cancel Appointment">
              <IconButton size="small" color="error"
                onClick={(e) => { e.stopPropagation(); handleCancelAppointment(appointment, e); }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ], [isAdmin, handleOpenModal, handleConfirmAppointment, handleAssignAgent, handleCancelAppointment]);
  

  const StatsCards = ({ cards = [], sx = {} }) => {
    return (
      <Grid container spacing={2} sx={{ mb: 2, ...sx }}>
        {cards.map((card, index) => (
          <Grid item xs={12} sm={6} md={12 / cards.length} key={index}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                ...(card.bgcolor && { bgcolor: card.bgcolor }),
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" sx={{ ...typography.bodyLarge}}>
                {card.label}
              </Typography>
              <Typography variant="h4" component="div" sx={{ ...typography.bodyLarge, mb: 1}}>
                {card.value}
              </Typography>
              {card.subtext && (
                <Typography variant="body2" sx={{ ...typography.bodyLarge}}>
                  {card.subtext}
                </Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  };
 


  return (
    <Box sx={{ position: "relative" }}>
      {alertMessage && (
        <Box
          sx={{
            position: "absolute",
            top: "-42px", // Adjust depending on actual header + search margin
            left: "280px", // Align with the end of search input
            zIndex: 999,
          }}
        >
          <Alert
            severity={alertSeverity}
            variant="filled"
            onClose={() => setAlertMessage(null)}
            sx={{
              height: "40px",
              alignItems: "center",
              px: 2,
              minWidth: "300px",
            }}
          >
            {alertMessage}
          </Alert>
        </Box>
      )}

      {/* Summary Cards */}
      <StatsCards
        cards={[
            { label: "Total Appointments", value: totalCount,     subtext: "All scheduled appointments", },
            { label: "Pending",            value: pendingCount,   subtext: "Awaiting confirmation",       bgcolor: "rgba(255, 235, 59, 0.1)" },
            { label: "Confirmed",          value: confirmedCount, subtext: "Ready to proceed",            bgcolor: "rgba(76, 175, 80, 0.1)"  },
            { label: "Cancelled",          value: cancelledCount, subtext: "No longer scheduled",         bgcolor: "rgba(244, 67, 54, 0.1)"  },
          ]}
        />

      {/* Filter Buttons */}
     <Box sx={{ mb: 2 }}>
        <CustomTabs
          value={filteredStatus}
          onChange={(event, newValue) => handleFilterChange(newValue)}
          tabs={[
            { label: "All",       value: "all"       },
            { label: "Pending",   value: "pending"   },
            { label: "Confirmed", value: "confirmed" },
            { label: "Cancelled", value: "cancelled" },
          ]}
        />
      </Box>

      {/* Appointments Table */}
      
      <AppTable
        columns={tableColumns}
        rows={appointmentsData}
        rowKey="id"
        emptyMessage="No appointments found"
        maxHeight={440}
        headCellSx={{ backgroundColor: "#f5f5f5", fontWeight: "bold" }}
        onRowClick={(appointment) => handleOpenModal(appointment)}
        rowClickable
        count={
          filteredStatus === "all"       ? totalCount
          : filteredStatus === "pending"   ? pendingCount
          : filteredStatus === "confirmed" ? confirmedCount
          : cancelledCount
        }
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[20]}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
 
      {/* Appointment Details Modal */}
      {selectedAppointment && (
        <Modal
          open={openModal}
          onClose={handleCloseModal}
          aria-labelledby="appointment-details-modal"
          aria-describedby="modal-showing-appointment-details"
        >
          <Box sx={modalStyle}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography id="modal-modal-title" variant="h6" component="h2">
                Appointment Details
              </Typography>
              <IconButton onClick={handleCloseModal} size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Divider />

            <Box
              sx={{
                mt: 2,
                mb: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="subtitle1">
                APT-{selectedAppointment.id}
              </Typography>
              <Chip
                label={selectedAppointment.appointment_status}
                color={
                  selectedAppointment.appointment_status === "Confirmed"
                    ? "success"
                    : selectedAppointment.appointment_status === "Cancelled"
                      ? "error"
                      : "warning"
                }
                size="small"
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{ display: "flex", alignItems: "center", mb: 1 }}
              >
                <PersonIcon fontSize="small" sx={{ mr: 1 }} />
                Client Information
              </Typography>
              <Box sx={{ ml: 4 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Name:</strong> {selectedAppointment.client_name}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Email:</strong>{" "}
                  {selectedAppointment.client_email || "N/A"}
                </Typography>
                <Typography variant="body2">
                  <strong>Phone:</strong> {selectedAppointment.phone_no}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{ display: "flex", alignItems: "center", mb: 1 }}
              >
                <EventIcon fontSize="small" sx={{ mr: 1 }} />
                Appointment Information
              </Typography>
              <Box sx={{ ml: 4 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Date:</strong> {formatDate(selectedAppointment.date)}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Time:</strong>{" "}
                  {formatTime(selectedAppointment.from_ts)} -{" "}
                  {formatTime(selectedAppointment.to_ts)}
                </Typography>
                <Typography variant="body2">
                  <strong>Created:</strong>{" "}
                  {formatWithTzLabel(selectedAppointment.created_at)}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 3,
              }}
            >
              {selectedAppointment.appointment_status !== "Confirmed" && (
                <Button
                  variant="contained"
                  color="success"
                  onClick={(e) =>
                    handleConfirmAppointment(selectedAppointment, e)
                  }
                >
                  Confirm Appointment
                </Button>
              )}

              {selectedAppointment.appointment_status !== "Cancelled" && (
                <Button
                  variant="contained"
                  color="error"
                  onClick={(e) =>
                    handleCancelAppointment(selectedAppointment, e)
                  }
                >
                  Cancel Appointment
                </Button>
              )}

              <Button variant="contained" onClick={handleCloseModal}>
                Close
              </Button>
            </Box>
          </Box>
        </Modal>
      )}

      {/* Appointment Conflict Modal */}
      {appointmentConflictModalOpen && (
        <Modal
          open={appointmentConflictModalOpen}
          onClose={handleCloseConflictAppointmentModal}
          aria-labelledby="conflict-appointment-modal"
        >
          <Box sx={modalStyle}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography id="modal-modal-title" variant="h6" component="h2">
                Conflicting Appointments
              </Typography>
              <IconButton
                onClick={handleCloseConflictAppointmentModal}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Divider />

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                You have the following conflicting appointments:
              </Typography>
              {conflictingAppointments.map((conflictingAppointment) => (
                <Box
                  key={conflictingAppointment.id}
                  sx={{
                    mb: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    border: "1px solid #ccc",
                    borderRadius: 1,
                    padding: 2,
                  }}
                >
                  <Typography variant="subtitle1">
                    <strong>{conflictingAppointment.title}</strong>
                  </Typography>
                  <Typography variant="subtitle2">
                    <strong>Start:</strong>{" "}
                    {formatWithTzLabel(conflictingAppointment.start)}
                  </Typography>
                  <Typography variant="subtitle2">
                    <strong>End:</strong>{" "}
                    {formatWithTzLabel(conflictingAppointment.end)}
                  </Typography>
                </Box>
              ))}
              <Typography variant="subtitle1" sx={{ mt: 1 }}>
                Are you sure you want to confirm this appointment?
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 3,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  updateAppointmentStatus(selectedAppointment.id, "confirm");
                  handleCloseConflictAppointmentModal();
                }}
              >
                Confirm
              </Button>

              <Button
                variant="contained"
                color="error"
                onClick={handleCloseConflictAppointmentModal}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Modal>
      )}

      {/* Assign Agent Modal */}
      {assignAgentModalOpen && agentOptions.length > 0 && (
        <Modal
          open={assignAgentModalOpen}
          onClose={handleCloseAssignAgentModal}
          aria-labelledby="assign-agent-modal"
        >
          <Box sx={modalStyle}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography id="modal-modal-title" variant="h6" component="h2">
                Assign Agent
              </Typography>
              <IconButton onClick={handleCloseAssignAgentModal} size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Divider />

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Assign an agent to the appointment{" "}
                <strong>APT-{selectedAppointment.id}</strong>?
              </Typography>

              <FormControl fullWidth size="small">
                <Select
                  value={selectedAgentOption}
                  onChange={(e) => setSelectedAgentOption(e.target.value)}
                  displayEmpty
                  renderValue={(selected) =>
                    selected ? selected.name : "Select an agent"
                  }
                >
                  {agentOptions.map((agentOption) => (
                    <MenuItem key={agentOption.id} value={agentOption}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        <Typography>
                          {agentOption.name || "N/A"}
                        </Typography>
                        <Typography variant="caption" color={ColorsConstants.primaryGray40}>
                          {agentOption.email || "N/A"}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 3,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                disabled={!selectedAgentOption}
                onClick={async () => {
                  await updateAgentAssignment();
                  handleCloseAssignAgentModal();
                }}
              >
                Assign Agent
              </Button>

              <Button
                variant="contained"
                color="error"
                onClick={handleCloseAssignAgentModal}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Modal>
      )}

      {/* Cancel Appointment Modal */}
      {cancelAppointmentModalOpen && (
        <Modal
          open={cancelAppointmentModalOpen}
          onClose={handleCloseCancelAppointmentModal}
          aria-labelledby="cancel-appointment-modal"
        >
          <Box sx={modalStyle}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography id="modal-modal-title" variant="h6" component="h2">
                Cancel Appointment?
              </Typography>
              <IconButton
                onClick={handleCloseCancelAppointmentModal}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Divider />

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Are you sure you want to cancel the appointment{" "}
                <strong> APT-{selectedAppointment.id}</strong>?
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 3,
              }}
            >
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  updateAppointmentStatus(selectedAppointment.id, "cancel");
                  handleCloseCancelAppointmentModal();
                }}
              >
                Cancel Appointment
              </Button>

              <Button
                variant="contained"
                onClick={handleCloseCancelAppointmentModal}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Modal>
      )}
    </Box>
  );
}
